import { DomainContext } from '../../changes/DomainContext';
import { ImageId } from '../../changes/ids';
import { domainTrace } from '../../diagnostics/domainTrace';
import { StaleReferenceError } from '../../errors/DomainError';
import { Image3D } from '../../patient/image3d';
import { Patient } from '../../patient/patient';
import { StructureSet } from '../../patient/structureset';

/** Lightweight identity passed from UI down to image commands. */
export interface ImageRef {
    readonly id: ImageId;
}

/**
 * Application-service seam for tracked writes to an {@link Image3D}.
 *
 * The surface is deliberately small. An image is the rendering substrate:
 * almost everything the UI does with one is a *read*, and its tracked
 * state is export status, aggregate membership, and the display label.
 * Window/level is transient display state written from the render loop
 * and is not routed through here — see {@link Image3D.setWindowLevel}.
 */
export class ImageService {
    constructor(
        private readonly context: DomainContext,
        private readonly patient: Patient
    ) {}

    public setExported(ref: ImageRef): void {
        domainTrace.service('ImageService.setExported', { id: ref.id });
        this.context.tracker.command(() => this.resolve(ref).setExported());
    }

    /**
     * Renames the series' display label (DICOM Series Description). A
     * tracked edit: the pending-changes store classifies it and the save
     * workflow PATCHes the database row (stored files stay untouched).
     */
    public rename(ref: ImageRef, seriesDescription: string): void {
        domainTrace.service('ImageService.rename', { id: ref.id });
        this.context.tracker.command(() =>
            this.resolve(ref).rename(seriesDescription)
        );
    }

    /**
     * Removes the image from the patient aggregate, cascading the unlink
     * to any structure set that referenced it.
     *
     * Callers should warn the user first when
     * {@link getAffectedStructureSets} is non-empty — deleting an image
     * silently detaches its structure sets, which is clinically
     * significant.
     */
    public remove(ref: ImageRef): void {
        domainTrace.service('ImageService.remove', { id: ref.id });
        this.context.tracker.command(() =>
            this.patient.removeImage(this.resolve(ref))
        );
    }

    /**
     * Structure sets that would be detached by removing this image.
     * A read, not a command — no changeset is emitted.
     */
    public getAffectedStructureSets(ref: ImageRef): StructureSet[] {
        return this.patient.getAffectedStructureSets(this.resolve(ref));
    }

    private resolve(ref: ImageRef): Image3D {
        const image = this.patient.images.find((i) => i.id === ref.id);
        if (!image) {
            throw new StaleReferenceError(`Image ${ref.id} no longer exists.`);
        }
        return image;
    }
}
